/**
 * BIRHAUS Undo Pattern Validator
 * 
 * Testing utilities for BIRHAUS Principle #5: "Undo over confirm"
 * 
 * Features:
 * - Confirmation dialog detection (anti-pattern)
 * - Undo functionality validation
 * - Undo timeout testing
 * - Destructive action pattern verification
 * - Spanish-first undo messaging
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
// Undo testing requires flexible event handling types

import { waitFor } from '@testing-library/react'
// import { screen, within, fireEvent } from '@testing-library/react' // Available for future test extensions
import userEvent from '@testing-library/user-event'

export interface UndoPatternViolation {
  type: 'confirmation-dialog' | 'missing-undo' | 'invalid-timeout' | 'poor-undo-ux' | 'missing-spanish'
  element: Element
  severity: 'minor' | 'moderate' | 'serious' | 'critical'
  message: string
  messageEs: string
  actionType?: 'delete' | 'destructive' | 'irreversible'
  birhausPrinciple: number
  suggestions: string[]
}

export interface UndoTestOptions {
  // Pattern detection
  detectConfirmationDialogs?: boolean
  validateUndoPresence?: boolean
  validateUndoTimeout?: boolean
  validateUndoMessaging?: boolean
  
  // Timeout settings
  defaultUndoTimeout?: number
  maxUndoTimeout?: number
  minUndoTimeout?: number
  
  // Destructive actions
  destructiveSelectors?: string[]
  destructiveTexts?: string[]
  
  // Spanish requirements
  requireSpanishUndoMessages?: boolean
  
  // Selectors
  confirmationSelectors?: string[]
  undoSelectors?: string[]
  destructiveActionSelectors?: string[]
}

export interface UndoTestResult {
  undoAvailable: boolean
  undoTimeout: number
  undoMessage: string
  undoMessageSpanish: boolean
  undoTriggered: boolean
  originalState: any
  undoState: any
}

export class UndoPatternValidator {
  private options: Required<UndoTestOptions>
  
  // Confirmation dialog anti-patterns
  private readonly confirmationPatterns = [
    /are\s+you\s+sure/i,
    /¿está.?\s+seguro/i,
    /confirm/i,
    /confirmar/i,
    /delete.*\?/i,
    /eliminar.*\?/i,
    /really.*delete/i,
    /realmente.*eliminar/i,
    /this.*cannot.*be.*undone/i,
    /esto.*no.*se.*puede.*deshacer/i
  ]
  
  // Destructive action patterns
  private readonly destructivePatterns = [
    /delete/i,
    /eliminar/i,
    /remove/i,
    /quitar/i,
    /destroy/i,
    /destruir/i,
    /clear/i,
    /limpiar/i,
    /reset/i,
    /restablecer/i,
    /purge/i,
    /purgar/i
  ]
  
  constructor(options: UndoTestOptions = {}) {
    this.options = {
      detectConfirmationDialogs: true,
      validateUndoPresence: true,
      validateUndoTimeout: true,
      validateUndoMessaging: true,
      defaultUndoTimeout: 10000, // 10 seconds
      maxUndoTimeout: 30000,     // 30 seconds max
      minUndoTimeout: 3000,      // 3 seconds min
      destructiveSelectors: [
        '[data-action="delete"]',
        '[data-action="remove"]',
        '.delete-button',
        '.remove-button',
        'button[class*="delete"]',
        'button[class*="remove"]'
      ],
      destructiveTexts: [
        'delete',
        'eliminar',
        'remove',
        'quitar',
        'destroy',
        'destruir'
      ],
      requireSpanishUndoMessages: true,
      confirmationSelectors: [
        '[role="alertdialog"]',
        '[role="dialog"]',
        '.confirmation-dialog',
        '.confirm-modal',
        '.alert-dialog'
      ],
      undoSelectors: [
        '[data-action="undo"]',
        '.undo-button',
        'button[class*="undo"]',
        '[aria-label*="undo"]',
        '[aria-label*="deshacer"]'
      ],
      destructiveActionSelectors: [
        'button[data-destructive="true"]',
        '.destructive-action',
        '[data-birhaus-component="button"][variant="destructive"]'
      ],
      ...options
    }
  }
  
  /**
   * Validate all undo pattern compliance
   */
  async validateAll(container: HTMLElement = document.body): Promise<UndoPatternViolation[]> {
    const violations: UndoPatternViolation[] = []
    
    if (this.options.detectConfirmationDialogs) {
      violations.push(...this.detectConfirmationDialogs(container))
    }
    
    if (this.options.validateUndoPresence) {
      violations.push(...this.validateUndoPresence(container))
    }
    
    if (this.options.validateUndoMessaging) {
      violations.push(...this.validateUndoMessaging(container))
    }
    
    return violations
  }
  
  /**
   * Detect confirmation dialogs (anti-pattern)
   */
  // eslint-disable-next-line max-lines-per-function -- Dialog detection requires comprehensive pattern matching
  detectConfirmationDialogs(container: HTMLElement): UndoPatternViolation[] {
    const violations: UndoPatternViolation[] = []
    
    // Check for confirmation dialogs
    this.options.confirmationSelectors.forEach(selector => {
      const dialogs = container.querySelectorAll(selector)
      
      dialogs.forEach(dialog => {
        const text = dialog.textContent || ''
        const hasConfirmationPattern = this.confirmationPatterns.some(pattern => 
          pattern.test(text)
        )
        
        if (hasConfirmationPattern) {
          violations.push({
            type: 'confirmation-dialog',
            element: dialog,
            severity: 'critical',
            message: 'Confirmation dialog detected - violates BIRHAUS Principle #5',
            messageEs: 'Diálogo de confirmación detectado - viola el Principio BIRHAUS #5',
            actionType: 'destructive',
            birhausPrinciple: 5,
            suggestions: [
              'Replace confirmation with undo functionality',
              'Use BirhausDialog with undo pattern',
              'Implement immediate action with undo option',
              'Show success message with undo button'
            ]
          })
        }
      })
    })
    
    // Check for window.confirm() usage in onclick handlers
    const elementsWithOnclick = container.querySelectorAll('[onclick]')
    elementsWithOnclick.forEach(element => {
      const onclick = element.getAttribute('onclick') || ''
      if (onclick.includes('confirm(')) {
        violations.push({
          type: 'confirmation-dialog',
          element: element,
          severity: 'critical',
          message: 'JavaScript confirm() detected in onclick handler',
          messageEs: 'confirm() de JavaScript detectado en manejador onclick',
          birhausPrinciple: 5,
          suggestions: [
            'Remove confirm() call from onclick',
            'Implement undo pattern instead',
            'Use BirhausButton with undo integration',
            'Add data-destructive attribute for proper handling'
          ]
        })
      }
    })
    
    return violations
  }
  
  /**
   * Validate undo functionality is present for destructive actions
   */
  validateUndoPresence(container: HTMLElement): UndoPatternViolation[] {
    const violations: UndoPatternViolation[] = []
    
    // Find destructive actions
    const destructiveActions = this.findDestructiveActions(container)
    
    destructiveActions.forEach(action => {
      const hasUndoSupport = this.checkUndoSupport(action)
      
      if (!hasUndoSupport) {
        const actionText = action.textContent?.trim() || ''
        const actionType = this.getActionType(actionText)
        
        violations.push({
          type: 'missing-undo',
          element: action,
          severity: actionType === 'delete' ? 'serious' : 'moderate',
          message: `Destructive action "${actionText}" lacks undo functionality`,
          messageEs: `Acción destructiva "${actionText}" carece de funcionalidad de deshacer`,
          actionType,
          birhausPrinciple: 5,
          suggestions: [
            'Add undo functionality to destructive action',
            'Use BirhausButton with destructive variant and undo',
            'Implement undo timeout with visual countdown',
            'Show success message with undo option'
          ]
        })
      }
    })
    
    return violations
  }
  
  /**
   * Validate undo messaging and timeout settings
   */
  // eslint-disable-next-line max-lines-per-function -- Undo messaging validation requires comprehensive checking
  validateUndoMessaging(container: HTMLElement): UndoPatternViolation[] {
    const violations: UndoPatternViolation[] = []
    
    // Find undo buttons and messages
    const undoElements = container.querySelectorAll(
      this.options.undoSelectors.join(', ')
    )
    
    undoElements.forEach(undoElement => {
      // Check for timeout information
      const hasTimeout = this.hasTimeoutInfo(undoElement)
      if (!hasTimeout) {
        violations.push({
          type: 'poor-undo-ux',
          element: undoElement,
          severity: 'moderate',
          message: 'Undo element lacks timeout information',
          messageEs: 'Elemento de deshacer carece de información de tiempo límite',
          birhausPrinciple: 5,
          suggestions: [
            'Add countdown timer to undo message',
            'Show remaining time for undo action',
            'Use BirhausDialog undo pattern with visual timer',
            'Display auto-close information'
          ]
        })
      }
      
      // Check for Spanish messaging
      if (this.options.requireSpanishUndoMessages) {
        const hasSpanishMessage = this.hasSpanishUndoMessage(undoElement)
        if (!hasSpanishMessage) {
          violations.push({
            type: 'missing-spanish',
            element: undoElement,
            severity: 'moderate',
            message: 'Undo element lacks Spanish messaging',
            messageEs: 'Elemento de deshacer carece de mensajes en español',
            birhausPrinciple: 7,
            suggestions: [
              'Add Spanish undo labels',
              'Use "Deshacer" instead of "Undo"',
              'Include Spanish timeout messages',
              'Add labelEs prop to undo components'
            ]
          })
        }
      }
    })
    
    return violations
  }
  
  /**
   * Test undo functionality for a specific action
   */
  // eslint-disable-next-line max-lines-per-function -- Undo functionality testing requires comprehensive state management
  async testUndoFunctionality(
    actionElement: Element,
    container: HTMLElement = document.body
  ): Promise<UndoTestResult> {
    const user = userEvent.setup()
    
    // Capture initial state
    const initialState = this.captureState(container)
    
    // Trigger the action
    await user.click(actionElement as HTMLElement)
    
    // Wait for undo UI to appear
    let undoButton: HTMLElement | null = null
    let undoTimeout = 0
    let undoMessage = ''
    let undoMessageSpanish = false
    
    try {
      // Look for undo button with timeout
      await waitFor(() => {
        undoButton = container.querySelector(
          this.options.undoSelectors.join(', ')
        ) as HTMLElement
        
        if (!undoButton) {
          throw new Error('Undo button not found')
        }
      }, { timeout: 5000 })
      
      if (undoButton) {
        // Extract timeout information
        undoTimeout = this.extractUndoTimeout(undoButton)
        undoMessage = (undoButton as HTMLElement)?.textContent?.trim() || ''
        undoMessageSpanish = this.isSpanishUndoMessage(undoMessage)
        
        // Test undo action
        // const _stateBeforeUndo = this.captureState(container) // Reserved for state comparison tests
        await user.click(undoButton)
        
        // Wait for undo to complete
        await waitFor(() => {
          const currentState = this.captureState(container)
          return this.statesEqual(currentState, initialState)
        }, { timeout: 3000 })
        
        const finalState = this.captureState(container)
        
        return {
          undoAvailable: true,
          undoTimeout,
          undoMessage,
          undoMessageSpanish,
          undoTriggered: true,
          originalState: initialState,
          undoState: finalState
        }
      }
    } catch (error) {
      // Undo not available or failed
    }
    
    return {
      undoAvailable: false,
      undoTimeout: 0,
      undoMessage: '',
      undoMessageSpanish: false,
      undoTriggered: false,
      originalState: initialState,
      undoState: this.captureState(container)
    }
  }
  
  // Helper methods
  
  private findDestructiveActions(container: HTMLElement): Element[] {
    const actions: Element[] = []
    
    // Find by selectors
    this.options.destructiveSelectors.forEach(selector => {
      actions.push(...Array.from(container.querySelectorAll(selector)))
    })
    
    // Find by destructive text patterns
    const buttons = container.querySelectorAll('button, [role="button"], a')
    buttons.forEach(button => {
      const text = button.textContent?.toLowerCase() || ''
      const isDestructive = this.destructivePatterns.some(pattern => 
        pattern.test(text)
      )
      
      if (isDestructive) {
        actions.push(button)
      }
    })
    
    // Remove duplicates
    return Array.from(new Set(actions))
  }
  
  private checkUndoSupport(action: Element): boolean {
    // Check for BIRHAUS undo attributes
    if (action.hasAttribute('data-undo-support')) {
      return true
    }
    
    // Check for BirhausButton with undo variant
    if (action.getAttribute('data-birhaus-component') === 'button') {
      const undoConfig = action.getAttribute('data-undo-config')
      return !!undoConfig
    }
    
    // Check for parent container with undo functionality
    const parent = action.closest('[data-undo-container]')
    return !!parent
  }
  
  private getActionType(actionText: string): UndoPatternViolation['actionType'] {
    const lowerText = actionText.toLowerCase()
    
    if (/delete|eliminar|destroy|destruir/.test(lowerText)) {
      return 'delete'
    }
    
    if (/remove|quitar|clear|limpiar|reset|restablecer/.test(lowerText)) {
      return 'destructive'
    }
    
    return 'irreversible'
  }
  
  private hasTimeoutInfo(element: Element): boolean {
    const text = element.textContent || ''
    const ariaLabel = element.getAttribute('aria-label') || ''
    const title = element.getAttribute('title') || ''
    
    const allText = `${text} ${ariaLabel} ${title}`.toLowerCase()
    
    // Look for timeout indicators
    const timeoutPatterns = [
      /\d+\s*(seconds?|segundos?)/i,
      /auto.*close/i,
      /cierre.*automático/i,
      /remaining|restante/i,
      /countdown|cuenta.*regresiva/i
    ]
    
    return timeoutPatterns.some(pattern => pattern.test(allText))
  }
  
  private hasSpanishUndoMessage(element: Element): boolean {
    const text = element.textContent || ''
    const ariaLabel = element.getAttribute('aria-label') || ''
    
    const spanishUndoPatterns = [
      /deshacer/i,
      /revertir/i,
      /cancelar.*acción/i,
      /volver.*atrás/i
    ]
    
    return spanishUndoPatterns.some(pattern => 
      pattern.test(text) || pattern.test(ariaLabel)
    )
  }
  
  public extractUndoTimeout(element: Element): number {
    // Try to extract timeout from various sources
    const timeoutAttr = element.getAttribute('data-undo-timeout')
    if (timeoutAttr) {
      return parseInt(timeoutAttr)
    }
    
    // Look for countdown in text
    const text = element.textContent || ''
    const match = text.match(/(\d+)\s*s/)
    if (match) {
      return parseInt(match[1]) * 1000
    }
    
    return this.options.defaultUndoTimeout
  }
  
  private isSpanishUndoMessage(message: string): boolean {
    const spanishPatterns = [
      /deshacer/i,
      /acción.*completada/i,
      /se.*deshizo/i,
      /revertido/i
    ]
    
    return spanishPatterns.some(pattern => pattern.test(message))
  }
  
  private captureState(container: HTMLElement): any {
    // Capture relevant state for comparison
    return {
      innerHTML: container.innerHTML,
      childElementCount: container.childElementCount,
      textContent: container.textContent,
      timestamp: Date.now()
    }
  }
  
  private statesEqual(state1: any, state2: any): boolean {
    // Simple state comparison - in real implementation, this would be more sophisticated
    return state1.childElementCount === state2.childElementCount &&
           state1.textContent === state2.textContent
  }
}

/**
 * Test helper functions for undo pattern assertions
 */

/**
 * Assert no confirmation dialogs are present
 */
export function expectNoConfirmationDialogs(
  container?: HTMLElement
): void {
  const validator = new UndoPatternValidator({ detectConfirmationDialogs: true })
  const violations = validator.detectConfirmationDialogs(container || document.body)
  
  if (violations.length > 0) {
    const errorMessages = violations
      .map(v => v.message)
      .join('\n')
    
    throw new Error(
      `Confirmation dialog anti-patterns detected:\n${errorMessages}\n\n` +
      'Use undo patterns instead of confirmation dialogs.'
    )
  }
}

/**
 * Assert destructive actions have undo functionality
 */
export function expectUndoForDestructiveActions(
  container?: HTMLElement
): void {
  const validator = new UndoPatternValidator({ validateUndoPresence: true })
  const violations = validator.validateUndoPresence(container || document.body)
  
  if (violations.length > 0) {
    const errorMessages = violations
      .map(v => v.message)
      .join('\n')
    
    throw new Error(
      `Missing undo functionality detected:\n${errorMessages}\n\n` +
      'All destructive actions must have undo functionality.'
    )
  }
}

/**
 * Test undo functionality for a specific action
 */
export async function expectUndoWorks(
  actionElement: Element,
  container?: HTMLElement
): Promise<void> {
  const validator = new UndoPatternValidator()
  const result = await validator.testUndoFunctionality(
    actionElement, 
    container || document.body
  )
  
  if (!result.undoAvailable) {
    throw new Error('Undo functionality not available for destructive action')
  }
  
  if (!result.undoTriggered) {
    throw new Error('Undo action failed to restore original state')
  }
  
  if (!result.undoMessageSpanish) {
    console.warn('Undo message not in Spanish - consider adding Spanish labels')
  }
}

/**
 * Assert undo timeout is reasonable
 */
export function expectReasonableUndoTimeout(
  undoElement: Element,
  minMs: number = 3000,
  maxMs: number = 30000
): void {
  const validator = new UndoPatternValidator()
  const timeout = validator.extractUndoTimeout(undoElement)
  
  if (timeout < minMs) {
    throw new Error(
      `Undo timeout ${timeout}ms is too short (minimum: ${minMs}ms)`
    )
  }
  
  if (timeout > maxMs) {
    throw new Error(
      `Undo timeout ${timeout}ms is too long (maximum: ${maxMs}ms)`
    )
  }
}

